import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { SHOP_TABS_BASE_PATH } from '../utils/pathsConstants';

import { Tab } from '../types/tabTypes';

const TABS_QUERY_KEY = ['/api/${SHOP_TABS_BASE_PATH}'];

export const useTabs = () => {
    const client = useQueryClient();
    const tabsQuery = useQuery({
        queryKey: TABS_QUERY_KEY,
        queryFn: async () => {
            const response = await axios.get<Tab[]>(
                `/api/${SHOP_TABS_BASE_PATH}`,
            );
            return response.data;
        },
    });

    const setTabActive = useMutation({
        mutationFn: async ({
            tabId,
            activeState,
        }: {
            tabId: number;
            activeState: boolean;
        }) => {
            const url = `/api/${SHOP_TABS_BASE_PATH}/${tabId}/toggleactive`;

            await axios.put(url, { setActive: activeState });
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: TABS_QUERY_KEY });
        },
    });

    const deleteTab = useMutation({
        mutationFn: async ({ tabId }: { tabId: number }) => {
            await axios.delete(`/api/${SHOP_TABS_BASE_PATH}/${tabId}`);
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: TABS_QUERY_KEY });
        },
    });

    const updateTabsOrder = useMutation({
        mutationFn: async ({ tabIds }: { tabIds: number[] }) => {
            return await axios.put(`/api/${SHOP_TABS_BASE_PATH}`, {
                ids: tabIds,
            });
        },
        onMutate: ({ tabIds }) => {
            client.setQueryData<Tab[]>(TABS_QUERY_KEY, (prevData) => {
                if (!prevData) {
                    return prevData;
                }

                return prevData.toSorted((tab1, tab2) => {
                    return tabIds.indexOf(tab1.id) - tabIds.indexOf(tab2.id);
                });
            });
        },
        onSettled: () => {
            client.invalidateQueries({ queryKey: TABS_QUERY_KEY });
        },
    });

    return { ...tabsQuery, setTabActive, deleteTab, updateTabsOrder };
};
